import React from 'react';
import clsx from 'clsx';

/**
 * EmptyState — canonical zero-data surface (REQ-SYS-EMPTY-01).
 *
 * TWO MODES — they are NOT the same and must never be conflated:
 *
 *   mode="first-run"  → there is genuinely no data yet. Show an inviting
 *                        headline + a PRIMARY call-to-action that creates the
 *                        first record ("Add your first customer").
 *
 *   mode="filtered"   → data exists but the current filter/search hides it.
 *                        Show a neutral "no matches" message + a SECONDARY
 *                        "Clear filters" affordance. NEVER show a create CTA
 *                        here — the user isn't empty, their filter is.
 *
 * Usage:
 *   <EmptyState
 *     mode="first-run"
 *     icon={UsersIcon}
 *     title="No customers yet"
 *     description="Add your first customer to start quoting."
 *     action={<Button onClick={onAdd}>Add customer</Button>}
 *   />
 *
 *   <EmptyState
 *     mode="filtered"
 *     title="No customers match your filters"
 *     onClearFilters={resetFilters}
 *   />
 */
export function EmptyState({
  mode = 'first-run',
  icon: Icon,
  title,
  description,
  action,
  onClearFilters,
  className,
}) {
  const isFiltered = mode === 'filtered';
  const resolvedTitle = title || (isFiltered ? 'No results match your filters' : 'Nothing here yet');

  return (
    <div
      className={clsx('flex flex-col items-center justify-center py-12 px-4 text-center', className)}
      role="status"
      data-empty-mode={mode}
    >
      {Icon && (
        <Icon
          className="h-12 w-12 mb-4"
          style={{ color: 'var(--color-text-muted)', opacity: 0.5 }}
          aria-hidden="true"
        />
      )}
      <h3
        className="mb-1"
        style={{
          fontFamily: 'var(--font-display)',
          fontSize: 'var(--text-h3, 1rem)',
          fontWeight: 600,
          color: 'var(--color-text-default)',
        }}
      >
        {resolvedTitle}
      </h3>
      {description && (
        <p
          className="text-sm mb-4 max-w-sm"
          style={{ color: 'var(--color-text-muted)' }}
        >
          {description}
        </p>
      )}

      {/* first-run → primary CTA;  filtered → neutral clear-filters affordance */}
      {!isFiltered && action}
      {isFiltered && onClearFilters && (
        <button
          type="button"
          onClick={onClearFilters}
          className="text-sm font-medium underline-offset-2 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-action-primary)] rounded"
          style={{ color: 'var(--color-action-primary)' }}
        >
          Clear filters
        </button>
      )}
    </div>
  );
}

export default EmptyState;
